import React, { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import GroupIcon from "@mui/icons-material/Group";
import FlagIcon from "@mui/icons-material/Flag";
import LayersIcon from "@mui/icons-material/Layers";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CircularProgress from "@mui/material/CircularProgress";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Button from "components/CustomButtons/Button.js";

// Context & API Services
import { useAuth } from "../../context/AuthContext";
import DownlinesApi from "../../api_calls/DownlinesApi";
import DownlinesSummaryApi from "../../api_calls/DownlinesSummaryApi";
import UserApi from "../../api_calls/AuthApi";
import Configs from "../../utils/Configs";
import AppConstants from "../../constants/appConstants";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 320,
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  p: 4,
  textAlign: "center",
};

const DownlinesList = () => {
  const { user: authUser } = useAuth();
  const userId = authUser?.user_id;
  const userRole = String(authUser?.userrole ?? "");
  const account = authUser?.account ?? "N/A";

  const shouldShowReferralLink = userRole !== "1" && userRole !== "4";

  const referralLink =
    shouldShowReferralLink && account !== "N/A"
      ? `${Configs.domain}/auth/register-page/?ref=${account}`
      : "";

  const [level, setLevel] = useState(1);
  const [downlines, setDownlines] = useState([]);
  const [downlinesBeyondLevel4, setDownlinesBeyondLevel4] = useState([]);
  const [loading, setLoading] = useState({
    level: false,
    beyond: false,
    summary: false,
    deleting: false,
  });
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [copied, setCopied] = useState(false);

  // Modal State
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    targetId: null,
    targetName: "",
  });

  useEffect(() => {
    if (!userId) return;
    fetchDownlinesSummary();
    fetchLevelDownlines(level);
    fetchBeyondLevel4();
  }, [level, userId]);

  const fetchDownlinesSummary = async () => {
    setLoading((prev) => ({ ...prev, summary: true }));
    try {
      const result = await DownlinesSummaryApi.getSummary(userId);
      if (result.status === "success") setSummary(result.data);
    } catch {
      setError("Error fetching downline summary.");
    } finally {
      setLoading((prev) => ({ ...prev, summary: false }));
    }
  };

  const fetchLevelDownlines = async (lvl) => {
    setLoading((prev) => ({ ...prev, level: true }));
    try {
      const result = await DownlinesApi.fetchDownlines(userId, lvl);
      if (result.status === "success") setDownlines(result.downlines || []);
    } catch {
      setDownlines([]);
    } finally {
      setLoading((prev) => ({ ...prev, level: false }));
    }
  };

  const fetchBeyondLevel4 = async () => {
    setLoading((prev) => ({ ...prev, beyond: true }));
    try {
      const result = await DownlinesApi.fetchDownlines(userId);
      if (result.status === "success")
        setDownlinesBeyondLevel4(result.downlinesBeyondLevel4 || []);
    } catch {
      setDownlinesBeyondLevel4([]);
    } finally {
      setLoading((prev) => ({ ...prev, beyond: false }));
    }
  };

  const handleCopyReferralLink = async () => {
    if (!referralLink) return;

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), AppConstants.TOAST_AUTO_DISMISS_MS);
    } catch (error) {
      console.error("Failed to copy referral link:", error);
    }
  };

  const handleOpenDelete = (member) => {
    setDeleteModal({
      open: true,
      targetId: member.user_id,
      targetName: `${member.first_name} ${member.other_names}`,
    });
  };

  const handleConfirmDelete = async () => {
    setLoading((prev) => ({ ...prev, deleting: true }));
    try {
      const result = await UserApi.deleteUser(deleteModal.targetId);
      if (result.status === "success") {
        fetchLevelDownlines(level);
        fetchDownlinesSummary();
        setDeleteModal({ open: false, targetId: null, targetName: "" });
      } else {
        const errorMsg =
          result.data === "cannot_delete_parent_user"
            ? "This member has active recruits and cannot be removed."
            : "Deletion failed.";
        alert(errorMsg);
      }
    } catch {
      alert("A system error occurred.");
    } finally {
      setLoading((prev) => ({ ...prev, deleting: false }));
    }
  };

  const renderUserCard = (member, isLevel1 = false) => {
    const isMarketer = userRole === "4" || userRole === "5";
    const showDelete = isMarketer && isLevel1;

    return (
      <li
        key={member.user_id || member.email}
        style={{
          padding: "16px",
          border: "1px solid #ddd",
          borderRadius: "12px",
          backgroundColor: "#fff",
          marginBottom: "12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <p style={{ margin: "4px 0" }}>
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              <GroupIcon fontSize="small" style={{ marginRight: "6px" }} />
              <strong>Name:</strong>&nbsp;{member.first_name}{" "}
              {member.other_names}
            </span>
          </p>
          <p style={{ margin: "4px 0" }}>
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              <FlagIcon fontSize="small" style={{ marginRight: "6px" }} />
              <strong>Country:</strong>&nbsp;{member.country_name}
            </span>
          </p>

          {!isMarketer && "subscription_paid" in member && (
            <p style={{ margin: "4px 0" }}>
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {member.subscription_paid === "yes" ? (
                  <CheckCircleIcon
                    fontSize="small"
                    color="success"
                    style={{ marginRight: "6px" }}
                  />
                ) : (
                  <CancelIcon
                    fontSize="small"
                    color="error"
                    style={{ marginRight: "6px" }}
                  />
                )}
                <strong>Subscription:</strong>&nbsp;
                {member.subscription_paid === "yes" ? "Paid" : "Not Paid"}
              </span>
            </p>
          )}
        </div>

        {showDelete && (
          <button
            onClick={() => handleOpenDelete(member)}
            style={{
              background: "none",
              border: "none",
              color: "#d32f2f",
              cursor: "pointer",
            }}
          >
            <DeleteOutlineIcon style={{ display: "block", margin: "0 auto" }} />
            <span style={{ fontSize: "10px", fontWeight: "bold" }}>DELETE</span>
          </button>
        )}
      </li>
    );
  };

  return (
    <div style={{ padding: "5px", maxWidth: "768px" }}>
      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: "20px",
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <GroupIcon sx={{ marginRight: "8px" }} /> My Referrals
      </Typography>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {summary && (
        <>
          <Typography sx={{ marginBottom: "12px", fontSize: "16px" }}>
            Referred by: {summary.parent_name}
          </Typography>

          {shouldShowReferralLink && (
            <>
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography sx={{ fontWeight: "bold", fontSize: "14px" }}>
                  My referral code
                </Typography>
              </Box>

              <Box
                sx={{
                  mb: 2,
                  p: 1,
                  backgroundColor: "#f7f7f7",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mr: 1,
                    wordBreak: "break-all",
                    flex: 1,
                  }}
                >
                  {referralLink || "Referral link unavailable"}
                </Typography>

                {referralLink && (
                  <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                    <IconButton
                      size="small"
                      onClick={handleCopyReferralLink}
                      color={copied ? "success" : "inherit"}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </>
          )}
        </>
      )}

      <div style={{ display: "flex", marginBottom: "16px" }}>
        {[1, 2, 3].map((lvl) => (
          <button
            key={lvl}
            onClick={() => setLevel(lvl)}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "8px 16px",
              marginRight: "12px",
              borderRadius: "8px",
              border: "1px solid #d76213",
              backgroundColor: level === lvl ? "#d76213" : "#f0f0f0",
              color: level === lvl ? "#fff" : "#333",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            <LayersIcon style={{ fontSize: "16px", marginRight: "6px" }} />
            <span>
              Level {lvl} ({summary ? summary[`lvl${lvl}`] : 0})
            </span>
          </button>
        ))}
      </div>

      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: "18px",
          marginTop: "24px",
          marginBottom: "12px",
        }}
      >
        Level {level} referrals
      </Typography>

      {loading.level ? (
        <p>Loading...</p>
      ) : downlines.length === 0 ? (
        <p>No referrals found</p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0 }}>
          {downlines.map((item) => renderUserCard(item, level === 1))}
        </ul>
      )}

      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: "18px",
          marginTop: "32px",
          marginBottom: "12px",
          borderTop: "1px solid #ccc",
          paddingTop: "16px",
        }}
      >
        Referrals beyond level 3
      </Typography>

      {loading.beyond ? (
        <p>Loading deeper referrals...</p>
      ) : downlinesBeyondLevel4.length === 0 ? (
        <p>No referrals found</p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0 }}>
          {downlinesBeyondLevel4.map((item) => renderUserCard(item, false))}
        </ul>
      )}

      <Modal
        open={deleteModal.open}
        onClose={() =>
          !loading.deleting && setDeleteModal({ ...deleteModal, open: false })
        }
      >
        <Box sx={modalStyle}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
            Confirm Deletion
          </Typography>
          <Typography sx={{ mb: 3 }}>
            Are you sure you want to remove{" "}
            <strong>{deleteModal.targetName}</strong> from your referrals?
          </Typography>
          <div
            style={{ display: "flex", justifyContent: "center", gap: "15px" }}
          >
            <Button
              color="transparent"
              onClick={() => setDeleteModal({ ...deleteModal, open: false })}
              disabled={loading.deleting}
            >
              Cancel
            </Button>
            <Button
              color="rose"
              onClick={handleConfirmDelete}
              disabled={loading.deleting}
            >
              {loading.deleting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Yes, Delete"
              )}
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default DownlinesList;
